import React, { useEffect, useState } from 'react';
import { ILineAmendmentLovs, ILineAmendmentProps } from '.';
import { useLazyQuery } from '@apollo/client';
import BeneficiaryWidget from '../components/policy-persons-widgets/BeneficiaryWidget';
import PayerOwnerWidget from '../components/policy-persons-widgets/PayerOwnerWidget';
import CostsAndCharges from '../components/CostsAndCharges';
import { getCoversPersonsQuery, getMedicalDetailsQuery } from '../queries';
import { mapToAmendmentDetails } from '../utils';
import { cloneDeep } from 'lodash';
import { getAmendmentInitialState } from '../content';
import { convertToPolicyPersons } from '../components/policy-persons-widgets/utils';
import Loader from '@components/loader/Loader';
import AmendmentDetails from '../components/AmendmentDetails';
import MedicalPrincipalInsured from '../components/policy-persons-widgets/MedicalPrincipalInsured';
import MedicalDetailsWidget from '../components/MedicalDetails';
import {
  enumListAsRecordObject,
  lookupListAsRecordObject,
} from '@utils/graph-utils';
import AmendmentPolicyDetails from '../components/AmendmentPolicyDetails';

const MedicalAmendment: React.FC<ILineAmendmentProps> = ({
  generalData,
  data,
  pageState,
  handleStateChange,
}) => {
  const amendmentData = data?.Production?.queries?.getAmendmentDetails;
  const [loading, setLoading] = useState<boolean>();
  const isDisabled = ['COMPLETED', 'CANCELLED', 'EXPIRED'].includes(
    generalData.amendment.status
  );

  const [lovs, setLovs] = useState<ILineAmendmentLovs>({
    amendmentTypes: {},
    amendmentSubTypes: {},
  });

  const [getCoversPersonsLazy] = useLazyQuery(getCoversPersonsQuery('persons'));

  const [getMedicalDetailsLazy] = useLazyQuery(getMedicalDetailsQuery, {
    variables: {
      selectedPolicyID: generalData.policy.id,
      version: generalData.policy.version,
    },
    errorPolicy: 'all',
  });

  const initialize = async () => {
    setLoading(true);

    const { data: detailsData } = await getMedicalDetailsLazy();

    const newValues = mapToAmendmentDetails({
      ...amendmentData?.[0],
      ...detailsData.Production.queries.GetPolicyMedical[0],
    });

    const newErrors = cloneDeep(
      getAmendmentInitialState(generalData.line.category).errors
    );
    const newTouched = cloneDeep(
      getAmendmentInitialState(generalData.line.category).touched
    );

    // Get covers and/or persons – start
    const { data: coversPersonsData } = await getCoversPersonsLazy({
      variables: {
        id: generalData.proposal.id,
        version: generalData.policy.version,
      },
      errorPolicy: 'all',
    });

    const persons = convertToPolicyPersons(coversPersonsData);
    newValues.policyPersons = persons;
    newErrors.policyPersons = {
      insured: persons.insured.map(() => newErrors['policyPersons'].insured[0]),
      payer: newErrors['policyPersons'].payer,
      owner: newErrors['policyPersons'].owner,
      beneficiaries: persons.beneficiaries.map(
        () => newErrors['policyPersons'].beneficiaries[0]
      ),
    };
    newTouched.policyPersons = {
      insured: persons.insured.map(
        () => newTouched['policyPersons'].insured[0]
      ),
      payer: newTouched['policyPersons'].payer,
      owner: newTouched['policyPersons'].owner,
      beneficiaries: persons.beneficiaries.map(
        () => newTouched['policyPersons'].beneficiaries[0]
      ),
    };
    // Get covers and/or persons – end

    setLovs({
      amendmentTypes: enumListAsRecordObject(
        data.Production_AmendmentTypes.enumValues
      ),
      amendmentSubTypes: lookupListAsRecordObject(
        data.Production.lookups.amendmentSubTypes,
        false,
        'Code',
        'Title'
      ),
    });

    handleStateChange({
      values: newValues,
      errors: newErrors,
      touched: newTouched,
    });
    setLoading(false);
  };

  useEffect(() => {
    if (data) {
      initialize();
    }
  }, [data]);

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <AmendmentDetails
        generalData={generalData}
        lovs={lovs}
        pageState={pageState}
        onChange={handleStateChange}
        isDisabled={isDisabled}
      />
      <AmendmentPolicyDetails
        generalData={generalData}
        lovs={lovs}
        pageState={pageState}
        onChange={handleStateChange}
        isDisabled={isDisabled}
      />
      <CostsAndCharges
        generalData={generalData}
        pageState={pageState}
        onPageStateUpdate={handleStateChange}
        isDisabled={isDisabled}
      />
      <PayerOwnerWidget
        generalData={generalData}
        pageState={pageState}
        onPageStateUpdate={handleStateChange}
        isDisabled={isDisabled}
      />
      <BeneficiaryWidget
        generalData={generalData}
        pageState={pageState}
        onPageStateUpdate={handleStateChange}
        isDisabled={isDisabled}
        maxNumber={
          data?.[0]?.policy_PlanID?.views?.PlanConfigManagement_all?.properties
            ?.MaxNbBeneficiary
        }
      />
      <MedicalDetailsWidget
        generalData={generalData}
        pageState={pageState}
        onPageStateUpdate={handleStateChange}
        isDisabled={isDisabled}
      />
      <MedicalPrincipalInsured
        generalData={generalData}
        pageState={pageState}
        onPageStateUpdate={handleStateChange}
        isDisabled={isDisabled}
        maxNumber={
          data?.[0]?.policy_PlanID?.views?.PlanConfigManagement_all?.properties
            ?.MaxNbInsured
        }
      />
    </>
  );
};

export default MedicalAmendment;
